import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';
import { EYesNo } from '../../../../../shared/models/types.enum';
import { IThreatAssessmentTeam } from '../../../../../shared/models/contact';
import { ContactsService } from '../../../../../shared/services/contact.service';
import { UserDataService } from '../../../../../shared/services/auth/userData.service';
import { ActivatedRoute } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SpinnerComponent } from '../../../../components/spinner/spinner.component';
import { DateUtils } from '../../../../../shared/helpers/dateUtils';

@Component({
  selector: 'rms-schedule-review',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SpinnerComponent
  ],
  templateUrl: './rms-schedule-review.component.html',
  styleUrl: './rms-schedule-review.component.css'
})
export class RmsScheduleReviewComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() rmsPlanReviewScheduleData: any;
  @Output() onSaveSection1 = new EventEmitter<any>();

  @ViewChild('datepickerReviewDate') datepickerReviewDate!: ElementRef;

  yesNo = EYesNo;
  rmsPlanReviewScheduleForm: FormGroup = new FormGroup([]);
  
  threatTeam: IThreatAssessmentTeam[] = [];
  usersList: IThreatAssessmentTeam[] = [];
  currentCaseId: string | null = null;
  currentBuildingId: string | null = null;
  selectedMemberId: number | null = null;
  messageModal: string | null = null;

  loadingContacts: boolean = false;
  loadingTeam: boolean = false;
  loading: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private contactsService: ContactsService,
    private userDataService: UserDataService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();

    this.loadUsers();
    this.initializeForm();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rmsPlanReviewScheduleData'] && this.rmsPlanReviewScheduleData) {
      this.selectedMemberId = this.rmsPlanReviewScheduleData?.review_coordinator || null;
      this.initializeForm();
    }
  }

  private initializeForm() {
    this.rmsPlanReviewScheduleForm = new FormGroup({
      review_session_date: new FormControl(this.rmsPlanReviewScheduleData?.review_session_date || DateUtils.formatDateForBackend(DateUtils.getCurrentFormattedDate()), Validators.required)
    });
  }  

  async ngAfterViewInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      // Dynamically import the Datepicker to avoid server-side loading
      const { Datepicker } = await import('flowbite-datepicker');
      if (this.datepickerReviewDate) {
        new Datepicker(this.datepickerReviewDate.nativeElement, {
          autohide: true,
          format: 'mm/dd/yyyy'
        });
        // set default value to display for incident date
        if (this.rmsPlanReviewScheduleData?.review_session_date) {
          // if date_of_incident is available, set it to the datepicker
          this.datepickerReviewDate.nativeElement.value = DateUtils.formatDateForDisplay(this.rmsPlanReviewScheduleData.review_session_date);
        } else {
          // if date_of_incident is not available, set it to the current date
          this.datepickerReviewDate.nativeElement.value = DateUtils.getCurrentFormattedDate();
        }
        this.datepickerReviewDate.nativeElement.addEventListener('changeDate', (event: any) => {
          const formattedDate = DateUtils.formatDateForBackend(event.target.value);
          this.rmsPlanReviewScheduleForm.get('review_session_date')?.setValue(formattedDate);
        });
      }
    }
  }

  // load all users
  loadUsers(): void {
    this.loadingContacts = true;
    this.updateLoadingState()
    this.contactsService.getUsers(this.currentBuildingId!).subscribe({
      next: (users: IThreatAssessmentTeam[]) => {
        this.usersList = users;
        // Sort the list after retrieving Users List.
        if (this.usersList.length > 0) {
          this.loadThreatAssessmentTeamMembers();
        }
        this.loadingContacts = false;
        this.updateLoadingState();
      },
      error: (error: any) => {
        this.loadingContacts = false;
        this.updateLoadingState();
        console.error('Error loading users:', error.message);
      }
    });
  };

  loadThreatAssessmentTeamMembers(): void {
    this.loadingTeam = true;
    this.updateLoadingState();
    if (this.currentCaseId) {
      this.contactsService.getThreatAssessmentTeam(this.currentCaseId).subscribe({
        next: (res) => {
          if (res.found !== false) {
            this.threatTeam = res.team
            .map((item: any) => this.usersList.find(contact => contact.user_id === item.user_id))
            .filter((contact: IThreatAssessmentTeam | undefined): contact is IThreatAssessmentTeam => contact !== undefined);
            if (this.threatTeam.length > 0) {
              this.threatTeam.sort((a, b) => a.name!.localeCompare(b.name!));
              this.selectedMemberId = this.rmsPlanReviewScheduleData?.review_coordinator ? this.rmsPlanReviewScheduleData.review_coordinator : this.threatTeam[0].user_id;
            }
            this.loadingTeam = false;
            this.updateLoadingState();
          }
        },
        error: (err) => {
          this.loadingTeam = false;
          this.updateLoadingState();
          console.error(err);
        }
      });
    }
  }

  updateLoadingState() {
    this.loading = this.loadingContacts || this.loadingTeam;
  }

  onMemberChange(event: any): void {
    this.selectedMemberId = event.target.value;
  };

  savePlanReviewSchedule(): void {
    if (this.rmsPlanReviewScheduleForm.valid && this.selectedMemberId) {
      const data = this.rmsPlanReviewScheduleForm.value;
      data['review_coordinator'] = this.selectedMemberId;
      this.onSaveSection1.emit(data);
    }
  }

}
